'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { ContextNavigation, ListPageSearch } from '@terra/ui-design-system';
import {
  REPORT_SOURCE_CHIP,
  type ReportSourceChip,
} from '../../../store/tab/reportsTabScopedFilterStore';
import { ListPageRefreshButton } from '../../common/ListPageRefreshButton';
import { REPORT_SOURCE_CHIPS } from '../reports-table/reports-list.model';

type ReportsContextNavigationProps = {
  sourceChip: ReportSourceChip;
  sourceCounts: Record<ReportSourceChip, number> | null;
  onSourceChipChange: (sourceChip: ReportSourceChip) => void;
  searchTerm: string;
  onSearchTermChange: (searchTerm: string) => void;
  onRefresh: () => void;
  isLoading: boolean;
};

export function ReportsContextNavigation({
  sourceChip,
  sourceCounts,
  onSourceChipChange,
  searchTerm,
  onSearchTermChange,
  onRefresh,
  isLoading,
}: ReportsContextNavigationProps) {
  const t = useTranslations('proxy.reports');
  const tCommon = useTranslations('designSystem.common');
  const items = useMemo(
    () =>
      REPORT_SOURCE_CHIPS.map((chip) => ({
        value: chip,
        label:
          chip === REPORT_SOURCE_CHIP.ALL
            ? tCommon('all')
            : t(`source.${chip}`),
        count: sourceCounts?.[chip],
      })),
    [sourceCounts, t, tCommon],
  );

  return (
    <ContextNavigation<ReportSourceChip>
      ariaLabel={t('chips.label')}
      items={items}
      value={sourceChip}
      onValueChange={onSourceChipChange}
      actions={
        <>
          <ListPageSearch
            value={searchTerm}
            onValueChange={onSearchTermChange}
            placeholder={t('searchPlaceholder')}
          />
          <ListPageRefreshButton onRefresh={onRefresh} isLoading={isLoading} />
        </>
      }
    />
  );
}
